import { RefObject, useLayoutEffect, useState } from 'react';

import Popover from '@/components/Popover';
import styled from '@/styles';
import { TierBoundary } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { tierColor } from './tierColors';
import { clampScore, displayScore } from './score';
import PopoverSurface from './PopoverSurface';
import { PopoverMedal } from './icons';

const Content = styled(PopoverSurface, {
  padding: '16px 24px',
  width: '348px',
  // GRAVY-10347: never wider than the viewport, so on a very narrow window/high
  // zoom the popover shrinks to fit instead of running off an edge (the JS
  // clamp below keeps it inside the card; this is the last-resort width guard).
  maxWidth: 'calc(100vw - 16px)',
});
const Title = styled('div', {
  fontWeight: 700,
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
  marginBottom: '4px',
});
const Subtitle = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depInk,
  marginBottom: '16px',
  variants: {
    activation: { true: { fontStyle: 'italic', color: '$themeDarker' } },
  },
});
// GRAVY-10347 (c): an activation worker's score clamps at 75, which maps to the
// Gold tier — so the ring and badge legitimately render Gold. This note keeps
// the popover copy coherent with that Gold badge by framing it as an
// onboarding-stage tier, not a shift-earned one (avoids the two-mental-models
// clash between the Gold badge and the activation subtitle).
const ActivationNote = styled('div', {
  fontSize: '11px',
  color: '$themeDarker',
  marginBottom: '16px',
});
// Medal artwork beside the tier text, per the popover frame (37858-31304)
const TierSummary = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
});
const TierName = styled('div', {
  fontSize: '$md',
  fontWeight: 700,
  color: dependabilityV2Colors.depInk,
});
const TierScore = styled('div', {
  fontSize: '11px',
  color: dependabilityV2Colors.depInk,
});
const Bar = styled('div', {
  position: 'relative',
  display: 'flex',
  height: '6px',
  borderRadius: '100px',
  overflow: 'visible',
  marginBottom: '8px',
});
const Segment = styled('div', { height: '6px' });
const Marker = styled('div', {
  position: 'absolute',
  top: '-4px',
  width: '13px',
  height: '13px',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.2)',
  transform: 'translateX(-50%)',
});
const Labels = styled('div', {
  display: 'flex',
});
const LabelBlock = styled('div', { flex: 1, textAlign: 'right' });
const LabelName = styled('div', {
  fontSize: '$sm',
  fontWeight: 600,
  color: dependabilityV2Colors.depInk,
});
const LabelRange = styled('div', {
  fontSize: '11px',
  color: dependabilityV2Colors.depInk,
});

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  boundaries: TierBoundary[];
  score: number;
  tier: string;
  completedShifts: number;
  scoringMode: 'activation' | 'reliability';
  justify?: 'left' | 'right';
  // GRAVY-10347: the element the popover is anchored to (the tier pill's
  // wrapper). When provided, the popover is positioned with a measured,
  // viewport/card-clamped `fixed` placement so it can never cover the header or
  // slide under the sidebar at any width/zoom. Omitted (e.g. in unit tests) it
  // falls back to the static placement.
  anchorRef?: RefObject<HTMLElement | null>;
}

const rangeLabel = (b: TierBoundary, isFirst: boolean, isLast: boolean) => {
  if (isFirst) return `<${b.max + 1}`;
  if (isLast) return `${b.min}+`;
  return `${b.min}–${b.max}`;
};

// Segments render at EQUAL widths (not proportional to their score span), so
// the marker is placed piecewise: which segment the tier occupies, then the
// score's fractional position within that segment. The denominator spans to
// the NEXT tier's min (max - min + 1): a 2-decimal score like 89.5 is still
// Gold and must land inside the gold segment, not on the platinum boundary.
const markerLeft = (
  boundaries: TierBoundary[],
  score: number,
  tier: string,
): number => {
  const n = boundaries.length;
  if (n === 0) return 0;
  const clamped = clampScore(score);
  let idx = boundaries.findIndex((b) => b.tier === tier);
  if (idx === -1)
    idx = boundaries.findIndex((b) => clamped >= b.min && clamped <= b.max);
  if (idx === -1) idx = clamped < boundaries[0].min ? 0 : n - 1;
  const b = boundaries[idx];
  const span = Math.max(1, b.max - b.min + 1);
  const frac = Math.max(0, Math.min(1, (clamped - b.min) / span));
  return ((idx + frac) / n) * 100;
};

const segmentRadius = (i: number, count: number) => {
  if (i === 0) return '100px 0 0 100px';
  if (i === count - 1) return '0 100px 100px 0';
  return '0';
};

const TierPopover = ({
  isOpen,
  onRequestClose,
  boundaries,
  score,
  tier,
  completedShifts,
  scoringMode,
  justify = 'left',
  anchorRef,
}: Props) => {
  const tierLabel = boundaries.find((b) => b.tier === tier)?.label || tier;

  // GRAVY-10347: the shared Popover positions with static CSS only (align/justify
  // offsets) and has no collision handling — so `align="above"` covered the page
  // header and `justify="right"` slid the 348px popover left under the fixed
  // sidebar (which paints over it: same z-index, earlier stacking context). The
  // pill's own position also shifts as the badges above it wrap, so no static
  // choice is safe at every width. When an anchor is supplied we measure the pill
  // + card + viewport and place the popover with `fixed`, dropped BELOW the pill
  // (clears the header) and clamped inside the card AND the viewport (never
  // reaches the sidebar or an edge). Recomputed on open/resize/scroll.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchorEl = anchorRef?.current;
    if (!isOpen || !anchorEl) {
      setPos(null);
      return undefined;
    }

    const compute = () => {
      const box = document.querySelector<HTMLElement>(
        '[data-testid="tier-popover"]',
      );
      if (!anchorEl || !box) return;

      const PAD = 8;
      const GAP = 6;
      const a = anchorEl.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      const card = anchorEl
        .closest<HTMLElement>('[data-testid="dependability-v2-card"]')
        ?.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = b.width || 348;
      const height = b.height || 0;

      // Horizontal: right-align to the pill, then clamp inside the card, then a
      // final hard clamp inside the viewport (guarantees it never runs off an
      // edge nor left of the card into the sidebar column).
      let left = a.right - width;
      const cardMin = Math.max(PAD, (card ? card.left : 0) + PAD);
      const cardMax = (card ? card.right : vw) - PAD - width;
      if (cardMax >= cardMin) left = Math.min(Math.max(left, cardMin), cardMax);
      else left = cardMin;
      left = Math.max(PAD, Math.min(left, vw - PAD - width));

      // Vertical: below the pill (never over the header); flip above only if
      // there is genuinely no room below.
      let top = a.bottom + GAP;
      if (top + height > vh - PAD && a.top - GAP - height > PAD) {
        top = a.top - GAP - height;
      }

      setPos({ top: Math.round(top), left: Math.round(left) });
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [isOpen, anchorRef]);

  return (
    <Popover
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="lg"
      justify={justify}
      // the frame shows the popover floating above the pill
      align="above"
      {...(pos
        ? {
            css: { position: 'fixed', margin: 0 },
            style: {
              top: pos.top,
              left: pos.left,
              right: 'auto',
              bottom: 'auto',
            },
          }
        : {})}
    >
      <Content data-testid="tier-popover">
        <Title>Dependability tier</Title>
        {scoringMode === 'activation' ? (
          <Subtitle activation>
            Activation score, based on onboarding progress
          </Subtitle>
        ) : (
          <Subtitle>{`Based on ${completedShifts} shifts`}</Subtitle>
        )}
        <TierSummary>
          <PopoverMedal tier={tier} color={tierColor(tier)} />
          <div>
            <TierName>{`${tierLabel} tier`}</TierName>
            <TierScore>{`Score: ${displayScore(score)}`}</TierScore>
          </div>
        </TierSummary>
        {scoringMode === 'activation' && (
          <ActivationNote data-testid="activation-tier-note">
            {`This ${tierLabel} badge reflects onboarding progress — shift-based scoring begins after 5 shifts.`}
          </ActivationNote>
        )}
        <Bar>
          {boundaries.map((b, i) => (
            <Segment
              key={b.tier}
              css={{
                width: `${100 / boundaries.length}%`,
                backgroundColor: tierColor(b.tier),
                // the Marker is Bar's last child, so :last-child can't round
                // the final segment — set end caps explicitly by index
                borderRadius: segmentRadius(i, boundaries.length),
              }}
            />
          ))}
          <Marker
            data-testid="tier-popover-marker"
            style={{
              left: `${markerLeft(boundaries, score, tier)}%`,
              border: `1px solid ${tierColor(tier)}`,
            }}
          />
        </Bar>
        <Labels>
          {boundaries.map((b, i) => (
            <LabelBlock key={b.tier}>
              <LabelName>{b.label}</LabelName>
              <LabelRange>
                {rangeLabel(b, i === 0, i === boundaries.length - 1)}
              </LabelRange>
            </LabelBlock>
          ))}
        </Labels>
      </Content>
    </Popover>
  );
};

export default TierPopover;
